interface ApiResponse<T = any> {
    success: boolean;
    statusCode: number;
    message: string;
    data: T;
}

export interface TranslationData {
    language: string;
    translations: { [key: string]: string };
    count: number;
}

export interface IPrivacyPolicySection {
    content_text: string;
    content_html: string;
    _id: string;
}

export interface IPrivacyPolicyDocument {
    language: string;
    privacy_policy: {
        distributor_privacy_policy: IPrivacyPolicySection;
        disclaimer: IPrivacyPolicySection;
        data_privacy_terms: IPrivacyPolicySection;
        orbo_privacy_policy: IPrivacyPolicySection;
    };
    _id: string;
}

export interface ICountryData {
    _id?: string;
    country_name?: string;
    country_language?: string;
    country_language_code?: string;
    country_code?: string;
    sso_language_code?: string;
    samcam_language?: string;
    dial_code?: string;
}

export interface ICountryDataResponse {
    countryData?: ICountryData[];
    languageData?: ICountryData[];
    dial_code?: number;
}

interface SessionData {
    sessionId: string;
}

interface UploadUrlData {
    uploadSignedUrl: string;
}

interface SkinConcern {
    concern: string;
    score: number;
    riskLevel: number;
}

interface SkinAnalysisData {
    output_score: SkinConcern[];
    input_image: string;
    profile_id: string;
    profile_exists: boolean;
    annotations: { [key: string]: string }
}
export interface ErrorObject {
    errorId?: string;
    type?: "UI_EXCEPTION" | "API_FAILURE" | "UX_ERROR" | "PERFORMANCE";
    message?: string;
    stackTrace?: string;
    component?: string;
    action?: string;
    timestamp?: number;
    url?: string;
}

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import {UserProfileDistributor, UserProfileWithDistributor, UserSession, UserSessionUpdate, DeactivatedUser } from '../types';

interface VerifyOtpResponse {
    otpResponse: {
        is_email_verified: boolean;
    }
}

interface UpdateProfileRequest {
    userId: string;
    gender: string;
    age_range: string;
    skin_type: string;
}

interface UpdateUserProfileRequest {
    userId: string;
    phone_number?: string;
    first_name?: string;
    last_name?: string;
    language?: string;
    country_id?: string;
}


// Initialize the agent on application start.
/**
 * B&N Skin API Service class
 */
class ApiService {
    private sessionId: string | null = null;
    private token: string | null = null;
    private axiosInstance: AxiosInstance;
    private updateSessionQueue: Map<string, Promise<void>> = new Map();
    private static readonly MAX_QUEUE_SIZE = 50; // Maximum number of queued session updates
    // Simple shared cache for IP -> country lookup
    private static cache: { ip: string; country: string | null } | null = null;
    private static request: Promise<string | null> | null = null;
    private static ipPromise: Promise<string | null> | null = null;
    private static cachedIp: string | null = null;
    private static countryListPromise: Promise<ICountryDataResponse> | null = null;
    private static countryDataPromise: Promise<ICountryDataResponse> | null = null;
    private static readonly COUNTRY_CACHE_STORAGE_KEY = 'countryCache';

    // Global auth lock to ensure 401 is handled once
    private static isHandlingAuthError = false;

    // Global AbortController to cancel all in‑flight requests on first 401
    private static abortController: AbortController = new AbortController();

    static isAuthErrorHandling(): boolean {
        return ApiService.isHandlingAuthError;
    }

    private createAuthBlockedError(): Error & { errorType: 'AUTH_BLOCKED' } {
        const authBlockedError = new Error('Authentication expired') as Error & {
            errorType: 'AUTH_BLOCKED';
        };
        authBlockedError.errorType = 'AUTH_BLOCKED';
        return authBlockedError;
    }

    private handleAuthFailure(message?: string) {
        // Prevent multiple executions
        if (ApiService.isHandlingAuthError) return;

        ApiService.isHandlingAuthError = true;

        // Clear auth state
        this.token = null;
        this.sessionId = null;

        // Cancel all running requests
        ApiService.abortController.abort();

        // Create fresh controller
        ApiService.abortController = new AbortController();

        // Notify app
        if (typeof window !== "undefined") {
            window.dispatchEvent(
                new CustomEvent("token-expired", {
                    detail: {
                        message:
                            message || "Session expired",
                        flowType: this.#getFlowTypeFromUrl(),
                    },
                })
            );
        }
    }

    /**
     * Initialize the API service with client credentials
     */
    constructor(sessionId: string | null = null, token: string | null = null) {
        this.sessionId = sessionId;
        this.token = token;
        
        // Create axios instance with base configuration
        this.axiosInstance = axios.create({
            baseURL: import.meta.env.VITE_API_BASE_URL,
            timeout: 30000,
            headers: {
                'Content-Type': 'application/json',
                'x-client-id': import.meta.env.VITE_CLIENT_KEY,
                'x-api-key': import.meta.env.VITE_API_KEY,
            }
        });

        // Setup request interceptor to add dynamic headers and attach AbortController
        this.axiosInstance.interceptors.request.use((config) => {
            // Always use current values, not closure values
            // Axios guarantees headers is an AxiosHeaders instance; we just mutate it.
            if (this.sessionId && config.headers) {
                config.headers['x-session-id'] = this.sessionId;
            }

            if (this.token && config.headers) {
                config.headers['authorization'] = this.token;
            }

            // If a global auth error is being handled, block only authenticated requests.
            // Public endpoints (e.g. languages/translations) should continue to work.
            const hasAuthContext = Boolean(
                this.token ||
                (config.headers && 'authorization' in config.headers)
            );
            if (ApiService.isHandlingAuthError && hasAuthContext) {
                return Promise.reject(this.createAuthBlockedError());
            }

            // Attach AbortController signal to every request if not already set
            if (!config.signal) {
                config.signal = ApiService.abortController.signal;
            }

            return config;
        });

        // Setup response interceptor for error handling
        this.axiosInstance.interceptors.response.use(
            (response: AxiosResponse) => {
                // Check if response has the expected API structure
                if (response.data && !response.data.success) {
                    const errorMessage = response.data.message || 'Unknown error';
                    const statusCode = response.data.statusCode || response.status || 400;
                    
                    // Create enhanced error with status code
                    const apiError = new Error(`API Error: ${errorMessage}`) as Error & {
                        statusCode?: number;
                        isApiError?: boolean;
                    };
                    apiError.statusCode = statusCode;
                    apiError.isApiError = true;
                    throw apiError;
                }
                return response;
            },
            (error: unknown) => {
                // Enhanced error handling with categorization
                if (axios.isAxiosError(error) && error.response) {
                    const statusCode = error.response.status;
                    const errorMessage: string =
                        (error.response.data as { message?: string } | undefined)?.message ||
                        error.response.statusText ||
                        'Unknown server error';
                    const errorData = error.response.data;

                    const apiError = new Error(`API Error (${statusCode}): ${errorMessage}`) as Error & {
                        statusCode?: number;
                        isApiError?: boolean;
                        responseData?: unknown;
                        errorType?:
                            | 'AUTHENTICATION'
                            | 'AUTHORIZATION'
                            | 'SERVER_ERROR'
                            | 'RATE_LIMIT'
                            | 'CLIENT_ERROR';
                        isTokenExpired?: boolean;
                        isServerDown?: boolean;
                    };

                    apiError.statusCode = statusCode;
                    apiError.isApiError = true;
                    apiError.responseData = errorData;

                    // Categorize errors
                    if (statusCode === 401 || statusCode === 403) {
                        apiError.isTokenExpired = true;
                        apiError.errorType = 'AUTHENTICATION';

                        const backendCode =
                            (error.response.data as any)?.code;

                        const shouldLogout =
                            statusCode === 401 ||
                            statusCode === 403 ||
                            backendCode === "ACCOUNT_DEACTIVATED";

                        if (shouldLogout) {
                            this.handleAuthFailure(errorMessage);
                        }
                    } else if (statusCode >= 500 && statusCode < 600) {
                        // Server errors (500, 502, 503, etc.)
                        apiError.errorType = 'SERVER_ERROR';
                        apiError.isServerDown = statusCode === 503 || statusCode === 502;
                    } else if (statusCode === 429) {
                        // Rate limiting
                        apiError.errorType = 'RATE_LIMIT';
                    } else if (statusCode >= 400 && statusCode < 500) {
                        // Client errors
                        apiError.errorType = 'CLIENT_ERROR';
                    }

                    throw apiError;
                }

                if (axios.isAxiosError(error) && error.request && !error.response) {
                    // If this is an aborted request, mark as cancelled without firing network-error
                    if (error.code === 'ERR_CANCELED') {
                        const cancelledError = new Error(
                            'Request cancelled'
                        ) as Error & {
                            errorType?: 'CANCELLED';
                        };

                        cancelledError.errorType = 'CANCELLED';

                        return Promise.reject(cancelledError);
                    }

                    // Request was made but no response received (server down, network issue)
                    const networkError = new Error(
                        'Network Error: No response received from server. Please check your internet connection or try again later.'
                    ) as Error & {
                        isNetworkError?: boolean;
                        errorType?: 'NETWORK_ERROR';
                        isServerDown?: boolean;
                    };

                    networkError.isNetworkError = true;
                    networkError.errorType = 'NETWORK_ERROR';
                    networkError.isServerDown = true; // Likely server is down or unreachable

                    // Dispatch custom event for network errors
                    if (typeof window !== 'undefined') {
                        window.dispatchEvent(
                            new CustomEvent('network-error', {
                                detail: { message: networkError.message }
                            })
                        );
                    }

                    throw networkError;
                }

                // Something else happened (timeout, etc.) or non-Axios error
                if (axios.isAxiosError(error)) {
                    const requestError = new Error(`Request Error: ${error.message}`) as Error & {
                        errorType?: 'REQUEST_ERROR' | 'TIMEOUT_ERROR';
                        isTimeout?: boolean;
                    };

                    requestError.errorType = 'REQUEST_ERROR';

                    // Check if it's a timeout
                    if (
                        error.code === 'ECONNABORTED' ||
                        (typeof error.message === 'string' && error.message.includes('timeout'))
                    ) {
                        requestError.isTimeout = true;
                        requestError.errorType = 'TIMEOUT_ERROR';
                    }

                    throw requestError;
                }

                // Non-Axios error fallback
                if (
                    error instanceof Error &&
                    (error as Error & { errorType?: string }).errorType
                ) {
                    throw error;
                }

                throw new Error(
                    error instanceof Error ? error.message : 'Unknown error occurred during API request'
                );
            }
        );
    }

    /**
     * Set the session ID for subsequent requests
     */
    setSessionId(sessionId: string): void {
        this.sessionId = sessionId;
    }

    /**
     * Set the authentication token for subsequent requests
     */
    setToken(token: string): void {
        this.token = token;

        // Allow requests again after successful login
        ApiService.isHandlingAuthError = false;

        // Fresh controller after re-login
        ApiService.abortController =
            new AbortController();
    }

    /**
     * Generic request handler with error handling using axios
     */
    private async request<T>(
        endpoint: string,
        method: string = 'GET',
        data?: unknown
    ): Promise<T> {
        try {
            const config: AxiosRequestConfig = {
                url: endpoint,
                method: method.toLowerCase() as AxiosRequestConfig['method'],
            };

            if (data && method !== 'GET') {
                config.data = data;
            }

            const response = await this.axiosInstance.request<T>(config);
            return response.data;
        } catch (error) {
            if (error instanceof Error) {
                console.error(`Request failed: ${error.message}`);
                throw error;
            }
            throw new Error('Unknown error occurred during API request');
        }
    }



    #getIp = async (): Promise<string | null> => {
        if (ApiService.cachedIp) {
            return ApiService.cachedIp;
        }

        if (!ApiService.ipPromise) {
            ApiService.ipPromise = (async () => {
                try {
                    const response = await axios.get<{ ip: string }>(
                        'https://api.ipify.org?format=json',
                        { timeout: 2000 }
                    );
                    ApiService.cachedIp = response.data.ip;
                    return ApiService.cachedIp;
                } catch (ipifyError) {
                    console.warn('[ApiService] ipify failed, trying ipapi fallback:', ipifyError);
                    try {
                        const fallback = await axios.get<{ ip?: string }>(
                            'https://ipapi.co/json/',
                            { timeout: 3000 }
                        );
                        ApiService.cachedIp = fallback.data.ip ?? null;
                        return ApiService.cachedIp;
                    } catch (ipapiError) {
                        console.error('[ApiService] Failed to fetch IP from ipapi fallback:', ipapiError);
                        return null;
                    }
                }
            })().finally(() => {
                ApiService.ipPromise = null;
            });
        }

        return ApiService.ipPromise;
    };

    async getCountryNameFromIp(): Promise<string | null> {
        if (!ApiService.cache && typeof window !== 'undefined') {
            const stored = window.localStorage.getItem(ApiService.COUNTRY_CACHE_STORAGE_KEY);
            if (stored) {
                try {
                    const parsed = JSON.parse(stored) as { ip?: string; country?: string | null };
                    const hasValidIp = typeof parsed?.ip === 'string' && parsed.ip.length > 0;
                    const hasValidCountry = parsed?.country === null || typeof parsed?.country === 'string';

                    if (hasValidIp && hasValidCountry) {
                        ApiService.cache = {
                            ip: parsed.ip!,
                            country: parsed.country ?? null,
                        };
                        ApiService.cachedIp = parsed.ip!;
                    }
                } catch {
                    // Ignore invalid cache format
                }
            }
        }

        if (ApiService.cache?.ip) {
            return ApiService.cache.country;
        }

        if (ApiService.request) {
            return ApiService.request;
        }

        ApiService.request = (async () => {
            try {
                const ip = await this.#getIp();

                if (!ip) {
                    return ApiService.cache?.country ?? null;
                }

                if (ApiService.cache?.ip === ip) {
                    return ApiService.cache.country;
                }

                const response = await this.request<ApiResponse<{ country_name?: string; country_code?: string; ip_address?: string }>>(
                    '/api/skin/countryByIp',
                    'POST',
                    { ip_address: ip }
                );
                const country = response.data?.country_name ?? null;
                ApiService.cache = { ip, country };
                if (typeof window !== 'undefined') {
                    window.localStorage.setItem(
                        ApiService.COUNTRY_CACHE_STORAGE_KEY,
                        JSON.stringify(ApiService.cache)
                    );
                }

                return country;
            } catch (error) {
                console.error('Geo fetch failed:', error);
                return null;
            } finally {
                ApiService.request = null;
            }
        })();

        return ApiService.request;
    }

    /**
     * Get distributor ID from URL path (public method)
     * @returns distributor ID if found, null otherwise
     */
    getDistributorIdFromUrl = (): string | null => {
        const pathSegments = window.location.pathname.split('/');
        const customerIndex = pathSegments.indexOf('customer');
        if (customerIndex !== -1 && pathSegments[customerIndex + 1]) {
            return pathSegments[customerIndex + 1];
        }
        return null;
    };

    /**
     * Get flow type from URL path (public method)
     * @returns 'distributor' if distributor flow, 'customer' if customer flow
     */
    getFlowTypeFromUrl = (): 'distributor' | 'customer' => {
        return this.#getFlowTypeFromUrl();
    };

    /**
     * Check if current flow is distributor or customer based on URL path
     * @returns 'distributor' if distributor flow, 'customer' if customer flow
     */
    #getFlowTypeFromUrl = (): 'distributor' | 'customer' => {
        const pathSegments = window.location.pathname.split('/');
        
        // Check if URL contains 'distributor' path
        if (pathSegments.includes('distributor')) {
            return 'distributor';
        }
        
        // Check if URL contains 'customer' path
        if (pathSegments.includes('customer')) {
            return 'customer';
        }
        
        // Default to customer flow if no specific path is found
        return 'customer';
    };


    /**
     * Create a session for the skin analysis application
     */
    async createSession(device: string, browser: string, platform: string, device_id: string, language: string, userId?: string, role?: number, distributorIdFromSignup?: string, distributorId?: string): Promise<string> {
        try {
            const ip = await this.#getIp();
            
            const payload = { 
                device, 
                ip: ip ?? '',
                browser,
                device_id,
                platform,
                language,
                role: role || 2, // Use passed role or default to customer
                ...(userId && { user_id: userId }),
            };
            // Determine endpoint based on role and distributor ID
            let endpoint: string;
            
            if (role === 1) {
                // For distributor role, use distributorId from distributorSignIn response
                if (distributorIdFromSignup) {
                    endpoint = `/api/skin/user/session/register?distributor_id=${distributorIdFromSignup}`;
                } else {
                    endpoint = '/api/skin/user/session/register';
                }
            } else {
                // For customer role, use distributorId from parameter
                if (distributorId) {
                    endpoint = `/api/skin/user/session/register?distributor_id=${distributorId}`;
                } else {
                    endpoint = '/api/skin/user/session/register';
                }
            }

            const response = await this.request<ApiResponse<SessionData>>(endpoint, 'POST', payload);

            const sessionId = response.data.sessionId;
            this.setSessionId(sessionId);
            return sessionId;
        } catch (error: any) {
            console.error('Failed to create session:', error);
            throw error.message || "Failed to create session.";
        }
    }

    /**
     * Get a pre-signed URL for image upload
     */
    async #getImageUploadUrl(): Promise<string> {
        try {
            if (!this.sessionId) {
                throw new Error('Session ID is required. Please create a session first.');
            }

            const response = await this.request<ApiResponse<UploadUrlData>>("/api/skin/session/image/upload?file_ext=png");
            return response.data.uploadSignedUrl;
        } catch (error) {
            console.error('Failed to get image upload URL:', error);
            throw error;
        }
    }

    /**
     * Upload an image to the pre-signed URL
     */
    async uploadImage(imageFile: File | Blob): Promise<boolean> {
        try {
            const presignedUrl = await this.#getImageUploadUrl();
            
            // Use axios for image upload with different content type
            await axios.put(presignedUrl, imageFile, {
                headers: {
                    'Content-Type': imageFile.type || 'image/png',
                },
            });

            return true;
        } catch (error) {
            console.error('Failed to upload image:', error);
            throw error;
        }
    }

    /**
     * Send OTP for phone verification
     */
    async sendOtp( email?: string, first_name?: string, last_name?: string,): Promise<boolean> {
        try {
            if (!this.sessionId) {
                throw new Error('Session ID is required. Please create a session first.');
            }

            const payload = { 
                name: "HL-User-Name",
                ...(email && { email }),
                ...(first_name && { first_name }),
                ...(last_name && { last_name }),
                // ...(country_id && { country_id }),
                // ...(phone_number && { phone_number }),
            };
            const response = await this.request<ApiResponse>('/api/skin/user/auth/sendOTP', 'POST', payload);

            return response.success;
        } catch (error) {
            console.error('Failed to send OTP:', error);
            throw error;
        }
    }

    async sendOtpForgotPassword( email?: string): Promise<boolean> {
        try {
            if (!this.sessionId) {
                throw new Error('Session ID is required. Please create a session first.');
            }

            const payload = { 
                ...(email && { email }),
            };
            const response = await this.request<ApiResponse>('/api/skin/user/auth/forgotPassword/sendOTP', 'POST', payload);

            return response.success;
        } catch (error) {
            console.error('Failed to send OTP for forgot password:', error);
            throw error;
        }
    }

    /**
     * Reset Password
     */
    async resetPassword(email: string, password: string): Promise<boolean> {
        try {
            if (!this.sessionId) {
                throw new Error('Session ID is required. Please create a session first.');
            }

            const payload = { 
                email,
                password
            };
            const response = await this.request<ApiResponse>('/api/skin/user/resetPassword', 'POST', payload);

            return response.success;
        } catch (error) {
            console.error('Failed to reset password:', error);
            throw error;
        }
    }

    /**
     * Verify OTP
     */
    async verifyOtp(otp: string, email?: string): Promise<VerifyOtpResponse> {
        try {
            if (!this.sessionId) {
                throw new Error('Session ID is required. Please create a session first.');
            }

            const payload = { 
                otp,
                ...(email && { email })
            };
            const response = await this.request<ApiResponse<VerifyOtpResponse>>('/api/skin/user/auth/verifyOTP', 'POST', payload);

            return response.data;
        } catch (error) {
            console.error('Failed to verify OTP:', error);
            throw error;
        }
    }

    /**
     * Get skin analysis
     */
    async getSkinAnalysis(): Promise<SkinAnalysisData> {
        try {
            if (!this.sessionId ) {
                throw new Error('Session ID is required. Please create a session first.');
            }

            const response = await this.request<ApiResponse<SkinAnalysisData>>('/api/skin/session/analysis');
            return response.data;
        } catch (error) {
            console.error('Failed to get skin analysis:', error);
            throw error;
        }
    }

    /**
     * Get user profiles
     */
    async getUserProfiles(role?: 1 | 2): Promise<UserProfileWithDistributor[] | UserProfileDistributor[]> {
        try {
            if (!this.token) {
                throw new Error('Authentication token is required. Please sign in first.');
            }
    
            // The token will be automatically added by the axios interceptor
            if (role === 1) {
                const response = await this.request<ApiResponse<UserProfileDistributor[]>>('/api/skin/user/me');
                return response.data;
            } else {
                // Customer flow - return UserProfile type
                const response = await this.request<ApiResponse<UserProfileWithDistributor[]>>('/api/skin/user/me');
                return response.data;
            }
        } catch (error) {
            console.error('Failed to get user profiles:', error);
            throw error;
        }
    }

    /**
     * Update user profile
     */
    async updateUserProfile(profileData: UpdateProfileRequest): Promise<boolean> {
        try {
            if (!this.sessionId) {
                throw new Error('Session ID is required. Please create a session first.');
            }

            const response = await this.request<ApiResponse>('/api/skin/user/userUpdate', 'PUT', {
                ...profileData,
            });
            return response.success;
        } catch (error) {
            console.error('Failed to update user profile:', error);
            throw error;
        }
    }

    /**
     * Update user profile with phone number, name, and language
     * @param profileData User profile data to update
     * @returns Promise<boolean> Success status
     */
    async updateUserProfileDetails(profileData: UpdateUserProfileRequest): Promise<boolean> {
        try {
          if (!this.token) {
            throw new Error("Authentication token is required. Please login first.");
          }
      
          
          const payload: any = {
            userId: profileData.userId,
          };
          
          // Only include fields that are provided (not undefined)
          if (profileData.phone_number !== undefined) {
            payload.phone_number = profileData.phone_number;
          }
          if (profileData.first_name !== undefined) {
            payload.first_name = profileData.first_name;
          }
          if (profileData.last_name !== undefined) {
            payload.last_name = profileData.last_name;
          }
          
          // Include language if provided
          if (profileData.language !== undefined && profileData.language !== null && profileData.language !== '') {
            payload.language = profileData.language;
          }

          if (profileData.country_id !== undefined && profileData.country_id !== null && profileData.country_id !== '') {
            payload.country_id = profileData.country_id;
        }
          
          // If only language is being updated, API might require at least one other field
          // Send other fields as null to satisfy validation (backend should ignore null values)

      
          const response = await this.request<ApiResponse>(
            "/api/skin/user/updateUserProfile",
            "PUT",
            payload
          );
      
          return response.success;
      
        } catch (error) {
          console.error("Failed to update user profile details:", error);
          throw error;
        }
      }
      
    

    /**
     * Get product recommendations based on skin concerns
     */
    async getProductRecommendations(): Promise<any> {
        try {
            if (!this.sessionId) {
                throw new Error('Session ID is required. Please create a session first.');
            }

            const response = await this.request<ApiResponse>('/api/skin/product/recommendation');
            return response.data;
        } catch (error) {
            console.error('Failed to get product recommendations:', error);
            throw error;
        }
    }

    async submitFeedback(feedback: "GREEN" | "YELLOW" | "RED"): Promise<ApiResponse> {
        try {
            if (!this.sessionId) {
                throw new Error('Session ID is required. Please create a session first.');
            }

            const payload = { feedback };
            return await this.request<ApiResponse>('/api/skin/session/feedback', 'POST', payload);
        } catch (error) {
            console.error('Failed to save feedback:', error);
            throw error;
        }
    }

    async sendReport(): Promise<void> {
        try {
            if (!this.token) {
                throw new Error('Session ID is required. Please create a session first.');
            }

            await this.request<ApiResponse>('/api/skin/session/report', 'GET');
        } catch (error) {
            console.error('Failed to save feedback:', error);
            throw error;
        }
    }



    // async getSkinScores(profileId: string): Promise<any> {
    //     try {
    //         if (!this.sessionId) {
    //             throw new Error('Session ID is required. Please create a session first.');
    //         }

    //         const response = await this.request<ApiResponse>(
    //             `/profile/scores?profile_id=${profileId}`,
    //             'GET'
    //         );

    //         return response.data;
    //     } catch (error) {
    //         console.error('Failed to get skin scores:', error);
    //         throw error;
    //     }
    // }

    /**
     * Update session with screen time data
     * @param screenName Name of the screen user spent time on
     * @param timeSpentSeconds Time spent on the screen in seconds
     */
    async updateSession(screenName: string, timeSpentSeconds: number): Promise<void> {
        try {
            if (!this.sessionId) {
                throw new Error('Session ID is required. Please create a session first.');
            }

            // Create a unique key for this session update
            const updateKey = `${this.sessionId}_${screenName}`;
            
            // Check if there's already a pending update for this session and screen
            if (this.updateSessionQueue.has(updateKey)) {
                return this.updateSessionQueue.get(updateKey)!;
            }

            // Enforce queue size limit by removing oldest entry if queue is full
            if (this.updateSessionQueue.size >= ApiService.MAX_QUEUE_SIZE) {
                // Remove the oldest entry (first key in Map maintains insertion order)
                const firstKey = this.updateSessionQueue.keys().next().value;
                if (firstKey) {
                    this.updateSessionQueue.delete(firstKey);
                }
            }

            // Create a new promise for this update
            const updatePromise = this.performSessionUpdate(screenName, timeSpentSeconds);
            
            // Store the promise in the queue
            this.updateSessionQueue.set(updateKey, updatePromise);
            
            try {
                await updatePromise;
            } finally {
                // Remove from queue when done (success or failure)
                this.updateSessionQueue.delete(updateKey);
            }
        } catch (error) {
            console.error('Failed to update session:', error);
            throw error;
        }
    }

    /**
     * Performs the actual session update request
     * @param screenName Name of the screen user spent time on
     * @param timeSpentSeconds Time spent on the screen in seconds
     */
    private async performSessionUpdate(screenName: string, timeSpentSeconds: number): Promise<void> {
        await this.request<ApiResponse>('/api/skin/user/session/update', 'PATCH', {
            [`${screenName}_screen`]: timeSpentSeconds
        });
    }

    async updateSessionReport(skipped: boolean): Promise<void> {
        try {
            if (!this.sessionId) {
                throw new Error('Session ID is required. Please create a session first.');
            }

            await this.request<ApiResponse>('/api/skin/user/session/update', 'PATCH', {
                is_send_email_skipped: skipped
            });
        } catch (error) {
            console.error('Failed to update session:', error);
            throw error;
        }
    }

    /**
     * Report error to the server
     * @param errorObject Error object containing error details
     */
    async reportError(errorObject: ErrorObject): Promise<void> {
        try {
            if (!this.sessionId) {
                throw new Error('Session ID is required. Please create a session first.');
            }

            const {
                errorId,
                type = "API_FAILURE",
                message,
                stackTrace,
                component,
                action,
                timestamp,
                url,
            } = errorObject;

            const errorPayload: ErrorObject = {
                errorId: errorId || (crypto?.randomUUID?.() ?? `error-${Date.now()}`),
                type,
                message: message || "Unknown error",
                stackTrace,
                component: component || "ApiService",
                action: action || "unknown-action",
                timestamp: timestamp ?? Date.now(),
                url: url || window.location.href,
            };

            await this.request<ApiResponse>('/api/skin/user/session/update', 'PATCH', errorPayload);
        } catch (error) {
            console.error('Failed to report error:', error);
            throw error;
        }
    }

    /**
     * Update user session with profile information
     * @param sessionData User session update data
     */
    async updateUserSession(sessionData: UserSessionUpdate): Promise<void> {
        try {
            if (!this.sessionId) {
                throw new Error('Session ID is required. Please create a session first.');
            }

            await this.request<ApiResponse>('/api/skin/user/session/update', 'PATCH', sessionData);
        } catch (error) {
            console.error('Failed to update user session:', error);
            throw error;
        }
    }

    /**
     * Update guest visit for distributor tracking
     */
    async updateGuestVisit(distributorId?: string): Promise<void> {
        try {
            const endpoint = distributorId 
                ? `/api/skin/user/session/update/guestVisit?distributor_id=${distributorId}`
                : '/api/skin/user/session/update/guestVisit';
            await this.request<ApiResponse>(endpoint, 'PATCH', {});
        } catch (error) {
            console.error('Failed to update guest visit:', error);
            throw error;
        }
    }

    /**
     * Sign up a new user
     * @param email User's email address
     * @param first_name User's first name
     * @param last_name User's last name
     * @param password User's password
     * @param country_code User's country code
     */
    async signUp(email: string, first_name: string, last_name: string, password: string, phone_number: string, country_id: string, distributorId?: string): Promise<{token: string, userId: string}> {
        try {
            if (!this.sessionId) {
                throw new Error('Session ID is required. Please create a session first.');
            }
            
            const payload: any = {
                email,
                first_name,
                last_name,
                password, 
                phone_number,
                country_id,
            };

            if (distributorId) {
                payload.distributor_id = distributorId;
            }

            const response = await this.request<ApiResponse<{token: string, userId: string}>>('/api/skin/user/signUp', 'POST', payload);
            return response.data;
        } catch (error) {
            console.error('Failed to sign up user:', error);
            throw error;
        }
    }

    /**
     * Sign in existing user
     * @param email User's email address
     * @param password User's password
     */
    async signIn(email: string, password: string): Promise<{data: {user: {accessToken: string, userId: string}}}> {
        try {
            if (!this.sessionId) {
                throw new Error('Session ID is required. Please create a session first.');
            }

            const payload = {
                email,
                password
            };

            // Use axios directly to preserve error response structure for email/phone extraction
            // Create a temporary axios instance without the response interceptor to preserve error structure
            const tempAxios = axios.create({
                baseURL: import.meta.env.VITE_API_BASE_URL,
                timeout: 30000,
                headers: {
                    'Content-Type': 'application/json',
                    'x-client-id': import.meta.env.VITE_CLIENT_KEY,
                    'x-api-key': import.meta.env.VITE_API_KEY,
                }
            });

            // Add request interceptor to add session and token
            tempAxios.interceptors.request.use((config) => {
                if (this.sessionId) {
                    config.headers['x-session-id'] = this.sessionId;
                }
                if (this.token) {
                    config.headers['authorization'] = this.token;
                }
                return config;
            });

            try {
                const config: AxiosRequestConfig = {
                    url: '/api/skin/user/signIn',
                    method: 'post',
                    data: payload,
                };

                const response = await tempAxios.request<ApiResponse<{user: {accessToken: string, userId: string}}>>(config);
                
                // Check if response has the expected API structure
                if (response.data && !response.data.success) {
                    throw new Error(`API Error: ${response.data.message || 'Unknown error'}`);
                }
                
                return { data: response.data.data };
            } catch (axiosError: any) {
                // Preserve the axios error structure so email and phone_number can be extracted
                if (axiosError.response) {
                    // Create an error object that preserves all axios error properties
                    const preservedError: any = Object.create(Error.prototype);
                    preservedError.message = axiosError.response.data?.error?.message || 
                        axiosError.response.data?.message || 
                        axiosError.message || 
                        'Failed to sign in';
                    preservedError.name = 'SignInError';
                    
                    // Preserve the full error response structure
                    preservedError.response = axiosError.response;
                    preservedError.error = axiosError.response.data?.error || axiosError.response.data;
                    
                    // Also add direct access to data for easier extraction
                    if (axiosError.response.data?.error?.data) {
                        preservedError.data = axiosError.response.data.error.data;
                    } else if (axiosError.response.data?.data) {
                        preservedError.data = axiosError.response.data.data;
                    }
                    
                    
                    throw preservedError;
                }
                throw axiosError;
            }
        } catch (error: any) {
            console.error('Failed to sign in user:', error);
            throw error;
        }
    }

    async getAllUserSessions(userId: string): Promise<{data: {sessions: UserSession}}> {
        try {
            if (!this.token) {
                throw new Error('Authentication token is required. Please sign in first.');
            }

            const response = await this.request<ApiResponse<{sessions: UserSession}>>(
                `/api/skin/user/session/allSessions?userId=${userId}`,
                'GET',
            );
            return response;

        } catch (error) {
            console.error('Failed to get all user sessions:', error);
            throw error;
        }
    }

    /*
     * Get all distributor sessions
     * @returns Promise with distributor sessions data
     */
    /**
     * Get all distributor sessions
     * @param page_no Page number for pagination
     * @param switch_side Switch side filter ('CS' for customer, etc.)
     * @returns Promise with sessions data
     */
    async getAllDistributorSessions(page_no: number, switch_side: string): Promise<{data: {sessions: UserSession[]}}> {
        try {
            if (!this.token) {
                throw new Error('Authentication token is required. Please sign in first.');
            }

            const response = await this.request<ApiResponse<{sessions: UserSession[]}>>(
                `/api/skin/distributor/allSessions?page_no=${page_no}&switch_side=${switch_side}`,
                'GET'
            );
            return response;

        } catch (error) {
            console.error('Failed to get all distributor sessions:', error);
            throw error;
        }
    }

    async getSingleDistributorCustomer(userId: string, distributor_id: string): Promise<ApiResponse<{sessions: UserSession[]}>> {
        try {
            if (!this.token) {
                throw new Error('Authentication token is required. Please sign in first.');
            }

            const response = await this.request<ApiResponse<{sessions: UserSession[]}>>(
                `/api/skin/distributor/singleCustomers?userId=${userId}&distributor_id=${distributor_id}`,
                'GET'
            );

            return response;
        } catch (error) {
            console.error('Failed to get single distributor customer:', error);
            throw error;
        }
    }

    /**
     * Sign in distributor with Herbalife SSO code
     * @param codeAndState Combined authorization code and state from Herbalife SSO
     */
    async distributorSignIn(codeAndState: string): Promise<{token: string, distributor_id: string,}> {
        try {
            const payload = {
                code: codeAndState,
            };

            const response = await this.request<ApiResponse<{token: string, distributor_id: string}>>(
                '/api/skin/distributor/signIn',
                'POST',
                payload
            );
            return response.data;
        } catch (error) {
            console.error('Failed to sign in distributor:', error);
            throw error;
        }
    }

    /**
     * Sign out user
     */
    async signOut(): Promise<boolean> {
        try {
            if (!this.token) {
                throw new Error('Authentication token is required. Please sign in first.');
            }

            const response = await this.request<ApiResponse>('/api/skin/account/signOut', 'POST');

            // Note: cookies are cleared in the Layout component's handleSignOut function
            // No need to remove individual items here as everything is cleared
            this.token = null;
            
            return response.success;
        } catch (error) {
            console.error('Failed to sign out:', error);
            throw error;
        }
    }

    /**
     * Get country data including country codes and phone codes
     */
    async getCountryData(): Promise<ICountryDataResponse> {
        if (!ApiService.countryDataPromise) {
            ApiService.countryDataPromise = this.request<ApiResponse<ICountryDataResponse>>(
                '/api/skin/countryData',
                'GET'
            )
                .then((response) => response.data)
                .catch((error) => {
                    ApiService.countryDataPromise = null;
                    console.error('Failed to get country data:', error);
                    throw error;
                });
        }
        return ApiService.countryDataPromise;
    }
    
    async getLanguages(): Promise<ICountryDataResponse> {
        if (!ApiService.countryListPromise) {
            ApiService.countryListPromise = this.request<ApiResponse<ICountryDataResponse>>(
                '/api/skin/countryList',
                'GET'
            )
                .then((response) => response.data)
                .catch((error) => {
                    ApiService.countryListPromise = null;
                    console.error('Failed to get languages:', error);
                    throw error;
                });
        }
        return ApiService.countryListPromise;
    }

        /**
     * Get translations for a specific language
     * @param language Language code (e.g., 'en', 'es', 'fr')
     * @returns Promise with translation data
     */
        async getTranslations(language: string): Promise<ApiResponse<TranslationData>> {
            try {
                const response = await this.request<ApiResponse<TranslationData>>(`/api/skin/translations/${language}`, 'GET');
                return response;
            } catch (error) {
                console.error('Failed to fetch translations:', error);
                throw error;
            }
        }

        /**
         * Get privacy policy translations for a specific language
         * @param language Language code (e.g., 'en', 'es', 'fr')
         * @returns Promise with privacy policy translation data
         * 
         */
        async getPrivacyPolicyTranslations(language: string): Promise<ApiResponse<IPrivacyPolicyDocument>> {
            try {
                const response = await this.request<ApiResponse<IPrivacyPolicyDocument>>(`/api/skin/translations/${language}/privacy-policy`, 'GET');
                return response;
            } catch (error) {
                console.error('Failed to fetch privacy policy translations:', error);
                throw error;
            }
        }

        /**
         * Get PDF brochure translations for a specific language
         * @param language Language code (e.g., 'en', 'es', 'fr', 'el')
         * @returns Promise with PDF brochure translation data
         */
        async getPdfBrochureTranslations(language: string): Promise<ApiResponse> {
            try {
                const response = await this.request<ApiResponse>(`/api/skin/translations/${language}/pdf-brochure`, 'GET');
                return response;
            } catch (error) {
                console.error('Failed to fetch PDF brochure translations:', error);
                throw error;
            }
        }

        /**
         * Get product translations for a specific language
         * @param language Language code (e.g., 'en', 'es', 'fr')
         * @returns Promise with product translation data
         */
        async getProductTranslations(language: string): Promise<ApiResponse> {
            try {
                const response = await this.request<ApiResponse>(`/api/skin/translations/${language}/product-translations`, 'GET');
                return response;
            } catch (error) {
                console.error('Failed to fetch product translations:', error);
                throw error;
            }
        }

    /**
     * Get distributor report data
     * @returns Promise with distributor report data
     */
    async getDistributorReport(): Promise<ApiResponse<any>> {
        try {
            if (!this.token) {
                throw new Error('Authentication token is required. Please sign in first.');
            }

            const response = await this.request<ApiResponse<any>>(
                '/api/skin/session/distributor/report',
                'POST'
            );
            return response;
        } catch (error) {
            console.error('Failed to get distributor report:', error);
            throw error;
        }
    }

    /**
     * Deactivate a user
     * @param userId User ID to deactivate
     * @param comments Optional reason/comments for deactivation
     * @returns Promise with API response
     */
    async deactivateUser(userId: string, comments?: string): Promise<ApiResponse> {
        try {
            if (!this.token) {
                throw new Error('Authentication token is required. Please sign in first.');
            }

            const requestBody: { userId: string; comments?: string } = { userId };
            if (comments) {
                requestBody.comments = comments;
            }

            const response = await this.request<ApiResponse>(
                `/api/skin/distributor/deactivateUser`,
                'PUT',
                requestBody
            );
            return response;
        } catch (error) {
            console.error('Failed to deactivate user:', error);
            throw error;
        }
    }

    /**
     * Get all deactivated users for a distributor
     * @param pageNo Page number for pagination (default: 1)
     * @returns Promise with deactivated users data
     */
    async getDeactivatedUsers(pageNo: number = 1): Promise<ApiResponse<{
        result: DeactivatedUser[];
        total_users_count: number;
    }>> {
        try {
            if (!this.token) {
                throw new Error('Authentication token is required. Please sign in first.');
            }

            const response = await this.request<ApiResponse<{
                result: DeactivatedUser[];
                total_users_count: number;
            }>>(
                `/api/skin/distributor/deactivatedUserList?page_no=${pageNo}`,
                'GET'
            );
            return response;

        } catch (error) {
            console.error('Failed to get deactivated users:', error);
            throw error;
        }
    }

    /**
     * Activate a deactivated user
     * @param userId User ID to activate
     * @param distributorId Distributor ID
     * @returns Promise with API response
     */
    async activateUser(userId: string): Promise<ApiResponse> {
        try {
            if (!this.token) {
                throw new Error('Authentication token is required. Please sign in first.');
            }

            const response = await this.request<ApiResponse>(
                `/api/skin/distributor/reactivateUser`,
                'PUT',
                { userId }
            );
            return response;

        } catch (error) {
            console.error('Failed to activate user:', error);
            throw error;
        }
    }
}

export default ApiService;